import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { ChevronLeft, Diamond, Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// ─── Inline editable cell ─────────────────────────────────────────────────────
function EditableCell({ value, onSave, type = 'text', className = '' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  const save = () => {
    if (val !== value) onSave(val);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type={type}
          value={val}
          onChange={e => setVal(e.target.value)}
          className={`border border-blue-400 rounded px-1.5 py-0.5 text-sm w-24 focus:outline-none ${className}`}
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        />
        <button onClick={save} className="p-1 text-green-600 hover:text-green-700"><Check className="w-3.5 h-3.5" /></button>
        <button onClick={() => setEditing(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <span
      onClick={() => { setVal(value); setEditing(true); }}
      className="cursor-pointer hover:bg-blue-50 px-1 rounded group flex items-center gap-1"
    >
      {value}
      <Pencil className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 shrink-0" />
    </span>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-gray-800">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function DiamondSeriesMasters() {
  const navigate = useNavigate();

  const [shapeTypes, setShapeTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sieveSizes, setSieveSizes] = useState([]);

  const [newShape, setNewShape] = useState('');
  const [addingShape, setAddingShape] = useState(false);

  const [newCat, setNewCat] = useState({ name: '', sort_order: '' });
  const [addingCat, setAddingCat] = useState(false);

  const [newSieve, setNewSieve] = useState({ label: '', category_id: '', avg_weight_ct: '' });
  const [addingSieve, setAddingSieve] = useState(false);

  const load = useCallback(async () => {
    try {
      const [st, cat, sieve] = await Promise.all([
        api.get('/diamond-series/masters/shape-types'),
        api.get('/diamond-series/masters/categories'),
        api.get('/diamond-series/masters/sieve-sizes'),
      ]);
      setShapeTypes(st.data);
      setCategories(cat.data);
      setSieveSizes(sieve.data);
    } catch {
      toast.error('Failed to load masters');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Shape Types ────────────────────────────────────────────────────────────
  const handleAddShape = async () => {
    if (!newShape.trim()) return;
    setAddingShape(true);
    try {
      await api.post('/diamond-series/masters/shape-types', { name: newShape.trim() });
      setNewShape('');
      load();
      toast.success('Shape type added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAddingShape(false); }
  };

  const handleDeleteShape = async (id) => {
    if (!window.confirm('Delete this shape type?')) return;
    try {
      await api.delete(`/diamond-series/masters/shape-types/${id}`);
      load();
      toast.success('Deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleUpdateShapeName = async (id, name) => {
    try {
      await api.put(`/diamond-series/masters/shape-types/${id}`, { name });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ── Categories ─────────────────────────────────────────────────────────────
  const handleAddCat = async () => {
    if (!newCat.name.trim()) return;
    setAddingCat(true);
    try {
      await api.post('/diamond-series/masters/categories', {
        name: newCat.name.trim(),
        sort_order: parseInt(newCat.sort_order) || 0,
      });
      setNewCat({ name: '', sort_order: '' });
      load();
      toast.success('Category added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAddingCat(false); }
  };

  const handleDeleteCat = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/diamond-series/masters/categories/${id}`);
      load();
      toast.success('Deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ── Sieve Sizes ────────────────────────────────────────────────────────────
  const handleUpdateSieve = async (id, field, value) => {
    try {
      const update = {};
      update[field] = field === 'avg_weight_ct' ? parseFloat(value) : value;
      await api.put(`/diamond-series/masters/sieve-sizes/${id}`, update);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteSieve = async (id) => {
    if (!window.confirm('Delete this sieve size?')) return;
    try {
      await api.delete(`/diamond-series/masters/sieve-sizes/${id}`);
      load();
      toast.success('Deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAddSieve = async () => {
    if (!newSieve.label.trim() || !newSieve.category_id || !newSieve.avg_weight_ct) return;
    setAddingSieve(true);
    const cat = categories.find(c => c.id === newSieve.category_id);
    try {
      await api.post('/diamond-series/masters/sieve-sizes', {
        label: newSieve.label.trim(),
        category_id: newSieve.category_id,
        category_name: cat?.name || '',
        avg_weight_ct: parseFloat(newSieve.avg_weight_ct),
        sort_order: 999,
      });
      setNewSieve({ label: '', category_id: '', avg_weight_ct: '' });
      load();
      toast.success('Sieve size added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAddingSieve(false); }
  };

  // Group sieve sizes by category for display
  const sieveByCategory = categories.map(cat => ({
    ...cat,
    sizes: sieveSizes.filter(s => s.category_id === cat.id),
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/diamond-series')} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <Diamond className="w-5 h-5 text-blue-600" />
          <h1 className="text-base font-semibold text-gray-900">Diamond Series Masters</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">

        {/* Shape Types */}
        <Section title={`Shape Types (${shapeTypes.length})`}>
          <div className="p-3 space-y-2">
            {shapeTypes.map(s => (
              <div key={s.id} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded-lg">
                <EditableCell value={s.name} onSave={v => handleUpdateShapeName(s.id, v)} />
                <button
                  data-testid={`delete-shape-${s.id}`}
                  onClick={() => handleDeleteShape(s.id)}
                  className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {/* Add new shape */}
            <div className="flex items-center gap-2 pt-1">
              <Input
                data-testid="new-shape-input"
                value={newShape}
                onChange={e => setNewShape(e.target.value)}
                placeholder="New shape name"
                className="h-8 text-sm flex-1"
                onKeyDown={e => e.key === 'Enter' && handleAddShape()}
              />
              <Button
                data-testid="add-shape-btn"
                onClick={handleAddShape}
                disabled={addingShape || !newShape.trim()}
                size="sm"
                className="h-8 gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
            </div>
          </div>
        </Section>

        {/* Size Categories */}
        <Section title={`Size Categories (${categories.length})`}>
          <div className="p-3 space-y-1">
            {categories.map((c, i) => (
              <div key={c.id} className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded-lg text-sm">
                <span className="font-medium text-gray-800">{c.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">order: {c.sort_order}</span>
                  <button
                    data-testid={`delete-cat-${c.id}`}
                    onClick={() => handleDeleteCat(c.id)}
                    className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
            {/* Add new category */}
            <div className="flex items-center gap-2 pt-2">
              <Input
                data-testid="new-cat-name"
                value={newCat.name}
                onChange={e => setNewCat(p => ({ ...p, name: e.target.value }))}
                placeholder="Category name"
                className="h-8 text-sm flex-1"
              />
              <Input
                data-testid="new-cat-order"
                value={newCat.sort_order}
                onChange={e => setNewCat(p => ({ ...p, sort_order: e.target.value }))}
                placeholder="Order"
                type="number"
                className="h-8 text-sm w-16"
              />
              <Button
                data-testid="add-cat-btn"
                onClick={handleAddCat}
                disabled={addingCat || !newCat.name.trim()}
                size="sm"
                className="h-8 gap-1 shrink-0"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
            </div>
          </div>
        </Section>

        {/* Sieve Sizes (grouped by category) */}
        {sieveByCategory.map(cat => (
          <Section key={cat.id} title={`${cat.name} (${cat.sizes.length} sizes)`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-3 py-2 text-gray-500 font-medium">Label</th>
                    <th className="text-right px-3 py-2 text-gray-500 font-medium">Avg Wt (ct)</th>
                    <th className="text-right px-3 py-2 text-gray-500 font-medium">Pcs/ct</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {cat.sizes.map(s => (
                    <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2">
                        <EditableCell value={s.label} onSave={v => handleUpdateSieve(s.id, 'label', v)} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <EditableCell
                          value={String(s.avg_weight_ct)}
                          type="number"
                          onSave={v => handleUpdateSieve(s.id, 'avg_weight_ct', v)}
                          className="text-right w-20"
                        />
                      </td>
                      <td className="px-3 py-2 text-right text-gray-500">
                        {s.avg_weight_ct > 0 ? Math.round(1 / s.avg_weight_ct) : '—'}
                      </td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => handleDeleteSieve(s.id)}
                          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add new sieve size */}
            <div className="p-3 flex flex-wrap items-center gap-2 border-t border-gray-100">
              <Input
                value={newSieve.category_id === cat.id ? newSieve.label : ''}
                onChange={e => setNewSieve({ label: e.target.value, category_id: cat.id, avg_weight_ct: newSieve.category_id === cat.id ? newSieve.avg_weight_ct : '' })}
                placeholder="Label (+2-2.5)"
                className="h-8 text-sm w-28"
              />
              <Input
                value={newSieve.category_id === cat.id ? newSieve.avg_weight_ct : ''}
                onChange={e => setNewSieve(p => ({ ...p, category_id: cat.id, avg_weight_ct: e.target.value }))}
                placeholder="Avg wt"
                type="number"
                step="0.001"
                className="h-8 text-sm w-20"
              />
              <Button
                onClick={handleAddSieve}
                disabled={addingSieve || !(newSieve.category_id === cat.id && newSieve.label && newSieve.avg_weight_ct)}
                size="sm"
                className="h-8 gap-1 shrink-0"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
            </div>
          </Section>
        ))}

        <div className="h-8" />
      </div>
    </div>
  );
}
